import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", className: "bg-green-100 text-green-800" },
  denied: { label: "Denied", className: "bg-red-100 text-red-800" },
  consumed: { label: "Reset complete", className: "bg-slate-200 text-slate-700" },
};

export default function RecoveryRequestsPage() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState(null);  // request being decided
  const [decision, setDecision] = useState("approved");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/recovery-requests", { params: { status: statusFilter } });
      setItems(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDecide = (item, initial) => {
    setDecideOn(item);
    setDecision(initial);
    setNote("");
  };

  const submitDecision = async () => {
    if (!decideOn) return;
    setSaving(true);
    try {
      await api.patch(`/admin/recovery-requests/${decideOn.id}`, {
        status: decision, decision_note: note,
      });
      toast.success(`Request ${decision}`);
      setDecideOn(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="admin-recovery-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" data-testid="admin-recovery-back" className="text-xs text-slate-500 hover:text-[#2C557E] inline-flex items-center">
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back to dashboard
          </Link>
          <h1 className="font-heading text-3xl text-slate-900 mt-1 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#5E8B7E]" /> Account Recovery Requests
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Patients whose accounts were auto-locked after repeated failed logins. Approve only after
            verifying their identity (phone call, ID, etc.).
          </p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" data-testid="admin-recovery-refresh" className="rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="admin-recovery-filter" className="w-56 rounded-xl bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="approved">Approved (awaiting reset)</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
            <SelectItem value="consumed">Reset complete</SelectItem>
            <SelectItem value="all">All requests</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">
          {items.length} {items.length === 1 ? "request" : "requests"}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="admin-recovery-empty" className="bg-white rounded-xl border border-dashed border-[#E5EAE8] p-12 text-center text-sm text-slate-500">
          No requests to show.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const meta = STATUS_LABEL[it.status] || { label: it.status, className: "bg-slate-100 text-slate-700" };
            const created = it.created_at ? new Date(it.created_at).toLocaleString() : "—";
            return (
              <div
                key={it.id}
                data-testid={`admin-recovery-row-${it.id}`}
                className="bg-white rounded-2xl border border-[#E5EAE8] p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg text-slate-900">{it.patient_name}</h3>
                      <Badge className={`${meta.className} rounded-full text-xs`}>{meta.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {it.email} · {it.patient_phone || "no phone on file"} · {it.unique_id || "—"}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">Requested {created}</p>
                    {it.reason && (
                      <p className="mt-3 text-sm text-slate-700 bg-[#F4F7F6] rounded-lg p-3">
                        <b className="text-slate-500 text-xs uppercase tracking-wide">Patient note:</b><br />
                        {it.reason}
                      </p>
                    )}
                    {it.decision_note && (
                      <p className="mt-3 text-xs text-slate-500">
                        <b>Clinic note:</b> {it.decision_note}
                      </p>
                    )}
                  </div>
                  {it.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`admin-recovery-approve-${it.id}`}
                        size="sm"
                        onClick={() => openDecide(it, "approved")}
                        className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]"
                      >
                        Approve
                      </Button>
                      <Button
                        data-testid={`admin-recovery-deny-${it.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => openDecide(it, "denied")}
                        className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Deny
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!decideOn} onOpenChange={(o) => !o && setDecideOn(null)}>
        <DialogContent data-testid="admin-recovery-decide-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {decision === "approved" ? "Approve" : "Deny"} recovery request
            </DialogTitle>
            <DialogDescription>
              {decideOn && (
                <span>
                  {decideOn.patient_name} ({decideOn.email}).{" "}
                  {decision === "approved"
                    ? "They'll receive an email link to set a new password via OTP."
                    : "They'll receive an email saying the request was denied."}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-600">
              Note for the patient (optional)
            </label>
            <Textarea
              data-testid="admin-recovery-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={
                decision === "approved"
                  ? "e.g. 'Verified you by phone — please reset within 24 hours.'"
                  : "e.g. 'Couldn't verify your identity. Call the clinic.'"
              }
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOn(null)} className="rounded-full">
              Cancel
            </Button>
            <Button
              data-testid="admin-recovery-confirm-btn"
              onClick={submitDecision}
              disabled={saving}
              className={`rounded-full ${
                decision === "approved" ? "bg-[#5E8B7E] hover:bg-[#4A7065]" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {saving ? "Saving…" : decision === "approved" ? "Approve" : "Deny"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
